/**
 * 移出批次抽屉，**接入后端**（Q29）：输入号码 → 提交解析（`previewSortingBatchMoveOut`，按所在批次分组 / 无法移出）
 * → 执行移出（`moveOutOfSortingBatch`，只提交可移出的号码）→ 逐号码结果。
 * 包牌号整包移出；运单号解除该包裹的批次关联并从所在包牌拆出。只动本仓批次。
 * 执行后不写回页面 mock 状态（mock 批次 id 与真实批次 id 会撞号，Q28 / Q29）。
 */
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Drawer, message } from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MOVE_OUT_OF_SORTING_BATCH, MoveOutOfSortingBatchData, MoveResultRowApi, SORTING_BATCH_MOVE_REFETCH_QUERIES } from '../graphql/mutations/sortingBatchMove';
import {
    MoveOutItemApi,
    MoveOutRejectedApi,
    PREVIEW_SORTING_BATCH_MOVE_OUT,
    PreviewSortingBatchMoveOutData,
} from '../graphql/queries/previewSortingBatchMove';
import { BatchMoveResultCode } from '../types';
import { moveErrorMessage } from '../utils/moveErrorMessage';
import { isBagLikeNumber, splitNumbers } from '../utils/numberInputUtils';
import { MOVE_DRAWER_WIDTH, MoveNumberInput, useCopyNumbers } from './MoveInShared';
import { MoveResultNotices } from './MoveResultNotices';
import { MoveOutDrawerProps } from './overlayProps';

interface Preview {
    items: MoveOutItemApi[];
    rejected: MoveOutRejectedApi[];
}

interface Session {
    text: string;
    preview: Preview | null;
    /** 执行结果；非空即锁定表单 */
    executed: MoveResultRowApi[] | null;
}

const EMPTY_SESSION: Session = { text: '', preview: null, executed: null };

/** 无法移出 / 执行失败的一行 */
interface BadRow {
    number: string;
    isBag: boolean;
    currentBatch?: string;
    reason?: BatchMoveResultCode;
}

/** 可移出号码按所在批次分组，保持输入顺序 */
const groupByCurrentBatch = (items: MoveOutItemApi[]): { batchName: string; items: MoveOutItemApi[] }[] => {
    const order: string[] = [];
    const byBatch: Record<string, MoveOutItemApi[]> = {};
    items.forEach((i) => {
        const key = i.currentBatch.batchName;
        if (!byBatch[key]) {
            byBatch[key] = [];
            order.push(key);
        }
        byBatch[key].push(i);
    });
    return order.map((batchName) => ({ batchName, items: byBatch[batchName] }));
};

const ROW_CLASS = 'flex items-center gap-2.5 border-0 border-t border-solid border-[#f2f3f5] px-3 py-2 text-[12.5px]';
const rowBg = (index: number): string => (index % 2 ? 'bg-[#fcfcfd]' : 'bg-white');

export const MoveOutDrawer: React.FC<MoveOutDrawerProps> = ({ open, warehouseId, onClose }) => {
    const { t } = useTranslation();
    const copyNumbers = useCopyNumbers();
    const [session, setSession] = useState<Session>(EMPTY_SESSION);
    const [previewQuery, { loading: parsing }] = useLazyQuery<PreviewSortingBatchMoveOutData>(PREVIEW_SORTING_BATCH_MOVE_OUT, {
        fetchPolicy: 'no-cache',
    });
    const [moveOut, { loading: executing }] = useMutation<MoveOutOfSortingBatchData>(MOVE_OUT_OF_SORTING_BATCH, {
        refetchQueries: SORTING_BATCH_MOVE_REFETCH_QUERIES,
    });
    // 每次打开重置；在渲染期调整，避免先渲染上一次的内容
    const [lastOpen, setLastOpen] = useState(open);
    if (lastOpen !== open) {
        setLastOpen(open);
        if (open) setSession(EMPTY_SESSION);
    }

    const { text, preview, executed } = session;
    const done = !!executed;
    const okItems = preview?.items ?? [];
    const succeededRows = (executed ?? []).filter((r) => r.success);
    const rowByNumber = new Map((executed ?? []).map((r) => [r.number, r]));
    // 执行后「已移出」分组只放执行成功的号码；执行期失败的号码只出现在下方「无法移出」区块
    const groups = groupByCurrentBatch(done ? okItems.filter((i) => rowByNumber.get(i.number)?.success) : okItems);
    const failedRows = (executed ?? []).filter((r) => !r.success);
    const badRows: BadRow[] = [
        ...(preview?.rejected ?? []).map((r) => ({
            number: r.number, isBag: isBagLikeNumber(r.number), currentBatch: r.currentBatch?.batchName, reason: r.reason,
        })),
        ...failedRows.map((r) => ({ number: r.number, isBag: isBagLikeNumber(r.number), currentBatch: r.batchName ?? undefined, reason: r.resultCode ?? undefined })),
    ];

    const summary = preview
        ? t(done ? 'wms.sortingBatch.moveOut.parsedSummaryDone' : 'wms.sortingBatch.moveOut.parsedSummary', {
            total: okItems.length + preview.rejected.length,
            ok: done ? succeededRows.length : okItems.length,
            bad: done ? badRows.length : preview.rejected.length,
        }) ?? ''
        : '';

    const changeText = (value: string) => setSession({ text: value, preview: null, executed: null });

    const submit = async () => {
        const numbers = splitNumbers(text);
        if (!numbers.length) {
            message.warning(t('wms.sortingBatch.moveOut.emptyInput'));
            return;
        }
        const { data, error } = await previewQuery({ variables: { warehouseId, numbers } });
        const result = data?.previewSortingBatchMoveOut;
        if (error || !result) {
            message.error(moveErrorMessage(error, t));
            return;
        }
        setSession((s) => ({ ...s, preview: { items: result.items, rejected: result.rejected }, executed: null }));
    };

    const execute = async () => {
        if (done || !okItems.length) return;
        try {
            const { data } = await moveOut({ variables: { warehouseId, numbers: okItems.map((i) => i.number) } });
            const rows = data?.moveOutOfSortingBatch.rows ?? [];
            setSession((s) => ({ ...s, executed: rows }));
            const movedCount = rows.filter((r) => r.success).length;
            if (movedCount) message.success(t('wms.sortingBatch.moveOut.movedOut', { count: movedCount }));
            else message.warning(t('wms.sortingBatch.moveOut.noneMovedOut'));
        } catch (e) {
            message.error(moveErrorMessage(e, t));
        }
    };

    const kindOf = (isBag: boolean) => (isBag ? t('wms.sortingBatch.common.bag') : t('wms.sortingBatch.common.parcel'));

    const countText = (list: MoveOutItemApi[]): string => {
        const bags = list.filter((i) => i.isBag).length;
        const parcels = list.length - bags;
        const parts: string[] = [];
        if (bags) parts.push(t('wms.sortingBatch.moveOut.bagCount', { count: bags }) ?? '');
        if (parcels) parts.push(t('wms.sortingBatch.moveOut.parcelCount', { count: parcels }) ?? '');
        return parts.join(' · ');
    };

    let primary: React.ReactNode;
    if (done) {
        // key 区分实例：复用执行按钮时 antd 内部 loading 状态会滞后一帧
        primary = <Button key="done" type="primary" onClick={onClose}>{t('wms.sortingBatch.common.done')}</Button>;
    } else if (okItems.length) {
        primary = (
            <Button key="execute" type="primary" loading={executing} onClick={execute}>
                {t('wms.sortingBatch.moveOut.executeCount', { count: okItems.length })}
            </Button>
        );
    } else {
        primary = <Button type="primary" disabled>{t('wms.sortingBatch.moveOut.execute')}</Button>;
    }
    const footer = (
        <div className="flex justify-end gap-2.5">
            <Button onClick={onClose}>{t('wms.sortingBatch.moveOut.cancel')}</Button>
            {primary}
        </div>
    );

    return (
        <Drawer open={open} onClose={onClose} width={MOVE_DRAWER_WIDTH} title={t('wms.sortingBatch.moveOut.title')} footer={footer}>
            <div className="flex flex-col gap-3.5">
                <div className={done ? 'pointer-events-none opacity-60' : ''}>
                    <MoveNumberInput
                        ns="moveOut"
                        value={text}
                        summary={summary}
                        disabled={done || parsing}
                        onChange={changeText}
                        onSubmit={submit}
                    />
                </div>
                {groups.length ? (
                    <div className="text-[13px] font-medium text-[#1d2129]">
                        {done ? t('wms.sortingBatch.moveOut.groupLabelDone') : t('wms.sortingBatch.moveOut.groupLabel')}
                    </div>
                ) : null}
                {groups.map((g) => (
                    <div key={g.batchName} className="overflow-hidden rounded border border-solid border-[#e5e6eb]">
                        <div className="flex flex-wrap items-center gap-2.5 bg-[#f7f8fa] px-3 py-2.5">
                            <span className="text-[12.5px] text-[#4e5969]">{t('wms.sortingBatch.moveOut.currentBatch')}</span>
                            <span className="font-mono text-[13px] font-medium text-[#1d2129]">{g.batchName}</span>
                            <span className="text-xs text-[#86909c]">{countText(g.items)}</span>
                            <div className="flex-1" />
                            <span className="text-xs text-[#86909c]">{t('wms.sortingBatch.moveOut.afterMoveOut')}</span>
                        </div>
                        {g.items.map((i, k) => (
                            <div key={`${i.number}-${k}`} className={`${ROW_CLASS} ${rowBg(k)}`}>
                                <span className="w-10 flex-none text-[#86909c]">{kindOf(i.isBag)}</span>
                                <span className="min-w-0 flex-1 font-mono text-[#1d2129]">{i.number}</span>
                                <span className="font-mono text-xs text-[#86909c]">{i.destination}</span>
                                {done && rowByNumber.get(i.number)?.failedOrderCount ? (
                                    <span className="text-xs text-[#c26b00]">
                                        {t('wms.sortingBatch.moveIn.failedOrders', { count: rowByNumber.get(i.number)?.failedOrderCount })}
                                    </span>
                                ) : null}
                            </div>
                        ))}
                    </div>
                ))}
                {badRows.length ? (
                    <div className="overflow-hidden rounded border border-solid border-[#fbd5d5]">
                        <div className="flex items-center gap-2.5 bg-[#fff5f5] px-3 py-2.5">
                            <span className="text-[13px] font-medium text-[#cb2634]">{t('wms.sortingBatch.moveOut.rejectTitle')}</span>
                            <div className="flex-1" />
                            <Button type="link" size="small" className="px-0" onClick={() => copyNumbers(badRows.map((i) => i.number))}>
                                {t('wms.sortingBatch.moveOut.copyAll')}
                            </Button>
                        </div>
                        {badRows.map((i, k) => (
                            <div key={`${i.number}-${k}`} className={`${ROW_CLASS} ${rowBg(k)}`}>
                                <span className="w-10 flex-none text-[#86909c]">{kindOf(i.isBag)}</span>
                                <span className="min-w-0 flex-1 whitespace-nowrap font-mono text-[#1d2129]">{i.number}</span>
                                {i.currentBatch ? (
                                    <span className="ml-2.5 min-w-0 flex-initial truncate font-mono text-xs text-[#86909c]">{i.currentBatch}</span>
                                ) : null}
                                <span className="flex-none whitespace-nowrap text-xs text-[#cb2634]">
                                    {i.reason ? t(`wms.sortingBatch.moveOut.reason.${i.reason}`) : null}
                                </span>
                                <Button type="link" size="small" className="px-0" onClick={() => copyNumbers([i.number])}>
                                    {t('wms.sortingBatch.common.copy')}
                                </Button>
                            </div>
                        ))}
                    </div>
                ) : null}
                {executed ? <MoveResultNotices rows={executed} /> : null}
                {done && succeededRows.length ? (
                    <div className="rounded border border-solid border-[#c6ecd3] bg-[#f0fbf4] px-3 py-2.5 text-[12.5px] text-[#1f7a3f]">
                        {t('wms.sortingBatch.moveOut.doneHint')}
                    </div>
                ) : null}
            </div>
        </Drawer>
    );
};
